import { navigateTo } from "common/navigation";

import {
  ActionIcon,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getCurrentPrices } from "api/report.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { formatNZD } from "common/utils";
import PriceListReportTypeSelect from "pages/admin/components/PriceListReportTypeSelection";
import ProductCodeInput from "pages/admin/components/ProductCodeInput";
import React, { useEffect, useRef, useState } from "react";
import { RptCurrentPrices } from "types/model.types";

const AdminPriceListReportPage: React.FC = () => {
  const [data, setData] = useState<RptCurrentPrices>([]);
  const [loading, setLoading] = useState(false);
  const [fromCode, setFromCode] = useState<number | undefined>();
  const [toCode, setToCode] = useState<number | undefined>();
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const apiFetchRef = useRef(false);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [reportType, setReportType] = useState<number>(0);

  const fetchData = async (download: boolean | null = false) => {
    if (apiFetchRef.current) return;
    apiFetchRef.current = true;
    setLoading(true);
    const startCode = fromCode ?? null;
    const endCode = toCode ?? null;

    if (download) {
      try {
        await safeApiCall(
          getCurrentPrices(startCode, endCode, null, null, true),
        );
        notifications.show({
          title: "File Download!",
          message: `Downloading CSV report`,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      } catch (err) {
        notifications.show({
          title: "File Download!",
          message: `Unable to export CSV for this report. ${err}`,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } else {
      const result = await safeApiCall(
        getCurrentPrices(startCode, endCode, pageSize, pageNumber),
      );

      setData(result?.data ?? []);
      setTotal(result?.total ?? 0);
    }

    setLoading(false);
    apiFetchRef.current = false;
  };

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize, selectedYear]);

  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Price List Report</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/price-list?rpt=${reportType}&f=${fromCode ?? ""}&t=${toCode ?? ""}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>
      <Group align="end" gap="sm">
        <PriceListReportTypeSelect
          name="PriceListReportType"
          label="Choose Report Type"
          title="Defaults to All if blank"
          value={reportType}
          setValue={setReportType}
        />

        <ProductCodeInput
          name="fromCode"
          label="From Product Code"
          value={fromCode}
          setValue={setFromCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <ProductCodeInput
          name="toCode"
          label="To Product Code"
          value={toCode}
          setValue={setToCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => fetchData(false)}
        >
          Refresh
        </Button>
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => {
            setFromCode(undefined);
            setToCode(undefined);
          }}
        >
          Clear Filters
        </Button>
        <Tooltip label="Download CSV">
          <ActionIcon size={"36"} onClick={() => fetchData(true)}>
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Divider label="Leave above filters blank if you want to report on all data" />
      <Group justify="space-between" wrap="nowrap" mb="md">
        <Group justify="flex-end" wrap="nowrap" mb="md">
          <Text size="sm" c="dimmed">
            Showing
          </Text>
          <Select
            value={pageSize.toString()}
            onChange={(val) => setPageSize(Number(val))}
            data={["25", "50", "100", "200"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Text> items per page</Text>
        </Group>
        <Pagination
          total={Math.ceil(total / pageSize)}
          value={pageNumber}
          onChange={setPageNumber}
          siblings={1}
          boundaries={1}
        />
      </Group>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Carton Price</Table.Th>
            {reportType !== 0 ? (
              <Table.Th>Each Price</Table.Th>
            ) : (
              <>
                <Table.Th>Retail</Table.Th>
                <Table.Th>SP1</Table.Th>
                <Table.Th>SP2</Table.Th>
                <Table.Th>SP3</Table.Th>
                <Table.Th>SP4</Table.Th>
              </>
            )}
            <Table.Th>Measure</Table.Th>
            <Table.Th>Barcode</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Center>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : (
            data.map((row, index) => (
              <Table.Tr key={`${index}-${row.id}`}>
                <Table.Td>{row.id}</Table.Td>
                <Table.Td>{row.description}</Table.Td>
                {/* 0: All, 1: Retail, 2: SP1, 3: SP2 */}
                {reportType === 1 ? (
                  <Table.Td>{formatNZD(row.retailCtnPrice)}</Table.Td>
                ) : reportType === 3 ? (
                  <Table.Td>{formatNZD(row.sellPrice2CtnPrice)}</Table.Td>
                ) : (
                  <Table.Td>{formatNZD(row.sellPrice1CtnPrice)}</Table.Td>
                )}
                {reportType !== 0 ? (
                  reportType === 1 ? (
                    <Table.Td>{formatNZD(row.retail)}</Table.Td>
                  ) : reportType === 3 ? (
                    <Table.Td>{formatNZD(row.sellPrice2)}</Table.Td>
                  ) : (
                    <Table.Td>{formatNZD(row.sellPrice1)}</Table.Td>
                  )
                ) : (
                  <>
                    <Table.Td>{formatNZD(row.retail)}</Table.Td>
                    <Table.Td>{formatNZD(row.sellPrice1)}</Table.Td>
                    <Table.Td>{formatNZD(row.sellPrice2)}</Table.Td>
                    <Table.Td>{formatNZD(row.sellPrice3)}</Table.Td>
                    <Table.Td>{formatNZD(row.sellPrice4)}</Table.Td>
                  </>
                )}
                <Table.Td>{`${row.unitsCtn} CTN`}</Table.Td>
                <Table.Td>{row.barcode}</Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
export default AdminPriceListReportPage;
